import Button from "@/components/base/Button";
import PageHero from "@/components/base/PageHero";
import Reveal from "@/components/base/Reveal";
import SectionHeading from "@/components/base/SectionHeading";
import Accordion, { type FaqItem } from "@/components/base/Accordion";
import { useUi } from "@/context/UiContext";
import Seo from "@/components/base/Seo";
import { faqPageSchema } from "@/lib/seo";

const CARD_TYPES = [
  { type: "方向型", text: "我不知道真正想要什麼。" },
  { type: "信念型", text: "我還沒開始，就先覺得自己做不到。" },
  { type: "啟動型", text: "我想很多，但就是開始不了。" },
  { type: "持續型", text: "我可以開始，但很難維持。" },
  { type: "能力型", text: "我想做，也願意做，但目前真的還不會。" },
];

const MAPPING = [
  { from: "方向型", to: "人生系統" },
  { from: "信念型", to: "信念系統" },
  { from: "啟動型／持續型", to: "行動系統" },
  { from: "能力型", to: "能力升級" },
];

const STARTS = ["免費開始", "入門體驗", "完整學習", "深度成長"];

const FAQ: FaqItem[] = [
  {
    q: "沒有明確目標也適合嗎？",
    a: "適合。很多人正是因為方向還不清楚才停滯不前。我們會先從理解你現在的位置與卡點開始，在過程中逐步釐清真正想要的方向。",
  },
  {
    q: "這是不是勵志課程？",
    a: "不是。幸福人才養成系統著重的是方法與系統，不是一時的打氣或情緒激勵。它幫助你看清楚問題、建立真正做得到的行動方式。",
  },
  {
    q: "很容易放棄怎麼辦？",
    a: "容易放棄，往往不是因為意志力不夠，而是行動方式不適合你。系統會從「做得到的小行動」開始，透過回饋與調整，逐步建立能持續的節奏。",
  },
  {
    q: "如果有嚴重心理困擾呢？",
    a: "幸福人才屬一般教育與個人成長內容，不取代心理治療、醫療診斷或其他專業服務。若你正面臨嚴重的心理困擾，建議先尋求專業協助。",
  },
];

export default function PersonalGrowth() {
  const { openQuiz, openAi } = useUi();

  return (
    <>
      <Seo
        title="個人成長｜行動力・拖延・目標・人生方向｜幸福人才"
        description="針對方向型、信念型、啟動型、持續型、能力型五類行動卡點，個人成長方案幫助你理解自己卡在哪裡，從做得到的小行動開始建立能持續的成長方式。"
        keywords="個人成長,行動力,拖延,目標管理,人生方向,行動卡點"
        jsonLd={faqPageSchema(FAQ)}
      />
      <PageHero
        eyebrow="個人成長"
        title={
          <>
            你不是做不到，
            <br />
            只是可能還沒找到真正卡住你的地方。
          </>
        }
        description={
          <>
            <p>
              我們很容易把問題歸因於不夠自律、沒有毅力、時間不夠、容易拖延。
              但真正影響能不能改變的原因，往往更深。先理解自己現在的位置，再決定下一步。
            </p>
          </>
        }
      >
        <Button onClick={openQuiz} icon="ri-arrow-right-line">
          30 秒找出我的行動卡點
        </Button>
      </PageHero>

      {/* 五種常見卡點 */}
      <section className="bg-white">
        <div className="mx-auto max-w-container px-4 md:px-6 py-14 md:py-20 lg:py-24">
          <Reveal>
            <SectionHeading
              eyebrow="五種常見卡點"
              title="同樣是「做不到」，背後可能完全不同。"
            />
          </Reveal>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {CARD_TYPES.map((c, i) => (
              <Reveal key={c.type} delay={i * 60}>
                <div className="h-full rounded-card bg-cream border border-line p-6 transition-transform duration-300 hover:-translate-y-1">
                  <p className="text-xs font-semibold text-gold uppercase tracking-wide">
                    {c.type}
                  </p>
                  <p className="mt-3 text-sm leading-[1.7] text-ink">{c.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={120}>
            <p className="mt-6 text-xs text-ink-soft/60">
              卡點不是人格分類，而是理解「現在卡在哪裡」的起點。
            </p>
          </Reveal>
        </div>
      </section>

      {/* 從卡點找到方法 */}
      <section className="bg-cream">
        <div className="mx-auto max-w-container px-4 md:px-6 py-14 md:py-20 lg:py-24">
          <Reveal>
            <SectionHeading eyebrow="從卡點找到方法" title="看懂卡點，才知道該從哪裡著手。" />
          </Reveal>
          <div className="mt-8 max-w-2xl">
            <div className="rounded-card bg-white border border-line overflow-hidden">
              {MAPPING.map((m, i) => (
                <div
                  key={m.from}
                  className={`flex items-center justify-between gap-4 px-6 py-4 ${
                    i < MAPPING.length - 1 ? "border-b border-line" : ""
                  }`}
                >
                  <span className="text-sm font-medium text-ink-soft">{m.from}</span>
                  <span className="flex items-center gap-3">
                    <i className="ri-arrow-right-line text-gold" aria-hidden="true"></i>
                    <span className="text-sm font-semibold text-primary">{m.to}</span>
                  </span>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-8">
            <Button to="/happiness-talent-system" variant="outline" icon="ri-arrow-right-line">
              了解幸福人才養成系統
            </Button>
          </div>
        </div>
      </section>

      {/* 實際案例情境 */}
      <section className="bg-white">
        <div className="mx-auto max-w-container px-4 md:px-6 py-14 md:py-20 lg:py-24">
          <Reveal>
            <SectionHeading
              eyebrow="情境示例"
              title="不一定要做得很大，先做得真實。"
            />
            <p className="mt-6 text-base leading-[1.85] text-ink-soft reading-width">
              以「建立運動習慣」為例，很多人一開始會訂下「每週運動五天」的目標，
              但沒多久就中斷。真正的轉折，往往是把它調整成「每週兩次，每次 15 分鐘」。
            </p>
            <div className="mt-8 rounded-card bg-primary-dark p-8 md:p-10">
              <p className="text-lg md:text-xl font-semibold text-white leading-[1.6]">
                開始建立一套自己真的做得到的方法。
              </p>
            </div>
            <p className="mt-6 text-xs text-ink-soft/60">
              * 此為情境示例，非真實學員案例。
            </p>
          </Reveal>
        </div>
      </section>

      {/* 我可以從哪裡開始 */}
      <section className="bg-cream">
        <div className="mx-auto max-w-container px-4 md:px-6 py-14 md:py-20 lg:py-24">
          <Reveal>
            <SectionHeading eyebrow="從哪裡開始" title="找一個適合你的起點。" />
          </Reveal>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {STARTS.map((s, i) => (
              <Reveal key={s} delay={i * 70}>
                <div className="h-full rounded-card bg-white border border-line p-6 transition-transform duration-300 hover:-translate-y-1">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-gold text-primary-dark text-sm font-bold">
                    {i + 1}
                  </div>
                  <p className="mt-4 text-base font-semibold text-ink">{s}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="mt-8">
            <Button to="/courses" icon="ri-arrow-right-line">
              查看課程與活動
            </Button>
          </div>
        </div>
      </section>

      {/* 真實回饋 */}
      <section className="bg-white">
        <div className="mx-auto max-w-container px-4 md:px-6 py-14 md:py-20 lg:py-24">
          <Reveal>
            <SectionHeading eyebrow="真實回饋" title="聽聽走過的人，怎麼說。" />
          </Reveal>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            {[0, 1].map((i) => (
              <Reveal key={i} delay={i * 90}>
                <div className="h-full rounded-card bg-cream border border-line p-7">
                  <span className="inline-flex items-center rounded-full bg-gold-soft/60 px-3 py-1 text-xs font-medium text-primary">
                    回饋待補
                  </span>
                  <div className="mt-5 flex items-center gap-1 text-gold">
                    {[0, 1, 2, 3, 4].map((s) => (
                      <i key={s} className="ri-star-fill text-sm" aria-hidden="true"></i>
                    ))}
                  </div>
                  <p className="mt-4 text-sm text-ink-soft/50">真實學員回饋內容建置中</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary-dark">
        <div className="mx-auto max-w-container px-4 md:px-6 py-14 md:py-20 lg:py-24">
          <Reveal>
            <div className="mx-auto max-w-2xl text-center">
              <h2 className="text-2xl md:text-[2.125rem] font-bold leading-[1.35] text-white">
                先看懂自己卡在哪裡，再開始改變。
              </h2>
              <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
                <Button onClick={openQuiz} icon="ri-arrow-right-line">
                  30 秒找出我的行動卡點
                </Button>
                <Button onClick={openAi} variant="outline-light">
                  和 AI 幸福人才顧問聊聊
                </Button>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white">
        <div className="mx-auto max-w-container px-4 md:px-6 py-14 md:py-20 lg:py-24">
          <Reveal>
            <SectionHeading eyebrow="常見問題" title="關於個人成長，你可能想知道。" />
          </Reveal>
          <Reveal delay={80}>
            <div className="mt-8 max-w-3xl">
              <Accordion items={FAQ} />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}